import React from 'react';
import './TimeField.css';

interface TimeFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
}

const HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

export const normalizeTimeValue = (value?: string | null): string => {
  if (!value) return '';

  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return '';

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return '';

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const compareTimeValues = (left: string, right: string): number => {
  const normalizedLeft = normalizeTimeValue(left);
  const normalizedRight = normalizeTimeValue(right);
  if (!normalizedLeft || !normalizedRight) return 0;
  return normalizedLeft.localeCompare(normalizedRight);
};

export const isValidActivityTimeRange = (start: string, end: string): boolean => {
  const normalizedStart = normalizeTimeValue(start);
  const normalizedEnd = normalizeTimeValue(end);
  if (!normalizedStart || !normalizedEnd) return true;
  return compareTimeValues(end, start) > 0;
};

const parseTimeValue = (value: string) => {
  const normalized = normalizeTimeValue(value);
  if (!normalized) {
    return { hour: '', minute: '' };
  }

  const [hour, minute] = normalized.split(':');
  return { hour, minute };
};

const TimeField: React.FC<TimeFieldProps> = ({ id, value, onChange }) => {
  const { hour, minute } = parseTimeValue(value);

  const updateTime = (nextHour: string, nextMinute: string) => {
    if (!nextHour && !nextMinute) {
      onChange('');
      return;
    }

    onChange(`${nextHour || '00'}:${nextMinute || '00'}`);
  };

  return (
    <div className="v2-time-field" id={id}>
      <select
        value={hour}
        onChange={(event) => updateTime(event.target.value, minute)}
        aria-label="Hour"
      >
        <option value="">--</option>
        {HOURS.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
      <span className="v2-time-field__separator" aria-hidden>:</span>
      <select
        value={minute}
        onChange={(event) => updateTime(hour, event.target.value)}
        aria-label="Minute"
      >
        <option value="">--</option>
        {MINUTES.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </div>
  );
};

export default TimeField;
